import axiosInstance from './axiosCreator';
import type {
  Assignment,
  AssignmentFilter,
  AssignmentsResponse,
} from '../types/Assignment';

interface FetchAssignmentsParams {
  filter?: AssignmentFilter;
  page?: number;
  limit?: number;
}

/**
 * Fetch the current user's assignments.
 *
 * Results are sorted by due date ascending by the backend.
 * - filter=open -> not completed
 * - filter=past -> completed or past-due
 * - filter=all  -> everything
 */
export const fetchAssignments = async ({
  filter = 'open',
  page = 1,
  limit = 20,
}: FetchAssignmentsParams = {}): Promise<AssignmentsResponse> => {
  const response = await axiosInstance.get<AssignmentsResponse>(
    '/assignments',
    {
      params: { filter, page, limit },
    }
  );

  return response.data;
};

/** The backend's page-size cap for GET /assignments. */
const ASSIGNMENT_LOOKUP_PAGE_SIZE = 100;
const ASSIGNMENT_LOOKUP_MAX_PAGES = 10;

/**
 * One of the current user's assignments, by id, or null when it is not
 * theirs (no by-id route exists, so this pages through `filter=all`). Used
 * by the LMS landing (ORA-1046), whose confirm answer names the assignment
 * but not its topic, exam or due date.
 */
export const findAssignmentById = async (
  assignmentId: string
): Promise<Assignment | null> => {
  for (let page = 1; page <= ASSIGNMENT_LOOKUP_MAX_PAGES; page += 1) {
    const response = await fetchAssignments({
      filter: 'all',
      page,
      limit: ASSIGNMENT_LOOKUP_PAGE_SIZE,
    });

    const match = response.assignments.find(
      (assignment) => assignment.id === assignmentId
    );

    if (match) return match;
    if (!response.meta.hasMore) return null;
  }

  return null;
};
